import { z } from 'zod';
import { optionalInput, phoneSchema } from '../api';
import { MODULE_CODES } from '../modules';
import { TIMEZONES } from '../regions';
import { SUPPORTED_COUNTRIES } from '../countries';
import { withoutBidiControls } from '../text';
import { reminderHoursSchema } from './operations';
import { PAID_NO_SHOW_POLICY } from '../appointment-transitions';
import { FOLLOW_UP_ENGINE, followUpValidDaysSchema } from '../follow-up-entitlement';
import { QUEUE_ORDER, SCREENING_BEFORE_READY } from '../opd-settings';
import { LAB_ACCESSION_MODES, LAB_ANALYSER_OVERRIDE, LAB_REPORT_LANGUAGES, LAB_REPORT_READY_SMS, LAB_TUBE_SUFFIXES } from '../lab-settings';
import { INSURANCE_MODES } from './insurance';
import { ipdScheduleSettingsSchema } from './ipd-orders';
import { STAFF_ALERT_SMS_DELAY_MAX, STAFF_ALERT_SMS_DELAY_MIN } from '../staff-alerts';
export const BRANCH_CATEGORY_CODES = [
    'opd', 'ipd', 'er', 'ot', 'lab', 'radiology', 'pharmacy', 'blood_bank', 'maternity', 'dialysis', 'daycare', 'dental',
];
export const letterheadLineSchema = z.object({
    text: z.string().trim().max(120),
    dir: z.enum(['ltr', 'rtl']).default('ltr'),
});
export const letterheadSchema = z.object({
    layout: z.enum(['single', 'bilingual']).default('single'),
    left: z.array(letterheadLineSchema).max(4).default([]),
    right: z.array(letterheadLineSchema).max(4).default([]),
    showBranchAddress: z.boolean().default(true),
    showBranchPhone: z.boolean().default(true),
    showBranchLicence: z.boolean().default(true),
    footer: z.string().trim().max(300).optional(),
});
export const upsertBranchSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(16)
        .regex(/^[A-Z0-9]+$/, 'Use uppercase letters and digits'),
    name: z.preprocess(withoutBidiControls, z.string().trim().min(2).max(160)),
    address: optionalInput(z.preprocess(withoutBidiControls, z.string().trim().max(400))),
    city: optionalInput(z.string().trim().max(80)),
    phone: optionalInput(phoneSchema),
    email: optionalInput(z.string().trim().email()),
    timezone: z.enum(TIMEZONES).optional(),
    country: z.enum(SUPPORTED_COUNTRIES).optional(),
    subdivision: z.preprocess((v) => (v === '' ? null : v), z.string().trim().toUpperCase().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/).nullable().optional()),
    nameLocal: z.preprocess((v) => (v === '' ? null : withoutBidiControls(v)), z.string().trim().max(160).nullable().optional()),
    addressLocal: z.preprocess((v) => (v === '' ? null : withoutBidiControls(v)), z.string().trim().max(400).nullable().optional()),
    letterheadLine: z.preprocess((v) => (v === '' ? null : v), z.string().trim().max(200).nullable().optional()),
    parentBranchId: z.string().uuid().nullable().optional(),
    categoryModules: z.array(z.enum(BRANCH_CATEGORY_CODES)).max(12).optional(),
    isActive: z.boolean().default(true),
});
const blankToNull = (value) => (value === '' ? null : withoutBidiControls(value));
export const branchTaxProfileSchema = z.object({
    registrationNo: z.preprocess(blankToNull, z.string().trim().max(40).nullable().optional()),
    subdivisionRegistrations: z.record(z.string().regex(/^[A-Z]{2}-[A-Z0-9]{1,3}$/), z.string().trim().max(40)).optional(),
    legalName: z.preprocess(blankToNull, z.string().trim().min(2).max(160).nullable().optional()),
    legalNameLocal: z.preprocess(blankToNull, z.string().trim().min(2).max(160).nullable().optional()),
    companyRegNo: z.preprocess(blankToNull, z.string().trim().max(40).nullable().optional()),
});
export function nextChildCode(parentCode, siblingCodes) {
    const pattern = new RegExp(`^${parentCode.replace(/[^A-Z0-9]/g, '')}(\\d+)$`);
    const highest = siblingCodes.reduce((max, code) => {
        const match = pattern.exec(code);
        return match ? Math.max(max, Number(match[1])) : max;
    }, 0);
    return `${parentCode}${highest + 1}`;
}
export const upsertDepartmentSchema = z.object({
    branchId: z.string().uuid(),
    code: z.string().trim().toUpperCase().min(2).max(16),
    name: z.string().trim().min(2).max(160),
    kind: z
        .enum(['clinical', 'diagnostic', 'pharmacy', 'support', 'administrative'])
        .default('clinical'),
    headUserId: z.string().uuid().nullable().optional(),
    isActive: z.boolean().default(true),
});
export const standardDepartmentsSchema = z.object({
    branchId: z.string().uuid(),
    codes: z.array(z.string().trim().toUpperCase().min(2).max(16)).min(1).max(60),
});
export const copyDepartmentsSchema = z.object({
    fromBranchId: z.string().uuid(),
    toBranchId: z.string().uuid(),
});
export const standardSpecialtiesSchema = z.object({
    codes: z.array(z.string().trim().toUpperCase().min(2).max(16)).min(1).max(60),
});
export const upsertSpecialtySchema = z.object({
    code: z.string().trim().toUpperCase().min(2).max(16).regex(/^[A-Z0-9_]+$/, 'Use uppercase letters, digits and underscores'),
    name: z.string().trim().min(2).max(120),
    nphiesPracticeCode: optionalInput(z.string().trim().max(40)),
    isActive: z.boolean().default(true),
});
export const upsertUnitSchema = z.object({
    departmentId: z.string().uuid(),
    name: z.string().trim().min(2).max(160),
    isActive: z.boolean().default(true),
});
export const usernameSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2)
    .max(40)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'Letters, digits, dot, dash and underscore only');
export const MAX_USER_DEPARTMENTS = 12;
export const userDepartmentIdsSchema = z
    .array(z.string().uuid())
    .max(MAX_USER_DEPARTMENTS)
    .transform((ids) => [...new Set(ids)]);
export function requestedDepartmentIds(input) {
    if (input.departmentIds !== undefined)
        return [...new Set(input.departmentIds)];
    if (input.departmentId === undefined)
        return undefined;
    return input.departmentId ? [input.departmentId] : [];
}
export const createUserSchema = z.object({
    name: z.string().trim().min(2).max(160),
    username: usernameSchema,
    email: optionalInput(z.string().trim().email()),
    phone: optionalInput(phoneSchema),
    password: z.string().min(10).max(200),
    designation: optionalInput(z.string().trim().max(120)),
    /** Doctors need a registration number for prescriptions and certificates. */
    registrationNo: optionalInput(z.string().trim().max(60)),
    registrationBody: optionalInput(z.string().trim().max(80)),
    nameLocal: z.preprocess((value) => (value === '' ? undefined : withoutBidiControls(value)), z.string().trim().max(160).optional()),
    specialtyId: z.string().uuid().nullable().optional(),
    defaultBranchId: z.string().uuid().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    departmentIds: userDepartmentIdsSchema.optional(),
    isDoctor: z.boolean().default(false),
    acceptsAppointments: z.boolean().optional(),
    isActive: z.boolean().default(true),
    discountLimitPct: z.number().min(0).max(100).default(0),
    refundApprovalLimitMinor: z.number().int().min(0).default(0),
    roles: z
        .array(z.object({
        roleId: z.string().uuid(),
        branchId: z.string().uuid().nullable().optional(),
        expiresAt: z.string().datetime().nullable().optional(),
    }))
        .default([]),
})
    // A doctor's registration number is printed on every prescription and
    // certificate they sign, so a doctor without one is a record the hospital
    // cannot legally issue.
    .refine((v) => !v.isDoctor || Boolean(v.registrationNo?.trim()), {
    path: ['registrationNo'],
    message: 'A doctor needs a registration number',
});
export const updateUserSchema = z.object({
    name: z.string().trim().min(2).max(160).optional(),
    email: z.string().email().nullable().optional(),
    phone: phoneSchema.nullable().optional(),
    designation: z.string().trim().max(120).nullable().optional(),
    registrationNo: z.string().trim().max(60).nullable().optional(),
    registrationBody: z.string().trim().max(80).nullable().optional(),
    nameLocal: z.preprocess((value) => (value === '' ? null : withoutBidiControls(value)), z.string().trim().max(160).nullable().optional()),
    specialtyId: z.string().uuid().nullable().optional(),
    defaultBranchId: z.string().uuid().nullable().optional(),
    departmentId: z.string().uuid().nullable().optional(),
    departmentIds: userDepartmentIdsSchema.optional(),
    isDoctor: z.boolean().optional(),
    acceptsAppointments: z.boolean().optional(),
    discountLimitPct: z.number().min(0).max(100).optional(),
    refundApprovalLimitMinor: z.number().int().min(0).optional(),
    roles: z
        .array(z.object({
        roleId: z.string().uuid(),
        branchId: z.string().uuid().nullable().optional(),
        expiresAt: z.string().datetime().nullable().optional(),
    }))
        .optional(),
});
export const roleAssignmentSchema = z.object({
    roleId: z.string().uuid(),
    branchId: z.string().uuid().nullable().optional(),
    expiresAt: z.string().datetime().nullable().optional(),
});
export const setUserRolesSchema = z.object({
    assignments: z.array(roleAssignmentSchema).max(50),
    basis: z.string().trim().min(1).max(64),
});
export function assignmentKey(assignment) {
    const expiresAt = assignment.expiresAt ? new Date(assignment.expiresAt).toISOString() : '';
    return `${assignment.roleId}|${assignment.branchId ?? ''}|${expiresAt}`;
}
export function assignmentBasis(assignments) {
    const canonical = assignments.map(assignmentKey).sort().join('\n');
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < canonical.length; i += 1) {
        const ch = canonical.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    const hex = (n) => (n >>> 0).toString(16).padStart(8, '0');
    return `${hex(h2)}${hex(h1)}${assignments.length.toString(16)}`;
}
export const setPermissionOverrideSchema = z.object({
    effect: z.enum(['grant', 'deny']),
    reason: z.string().trim().min(5).max(400),
});
export const upsertRoleSchema = z.object({
    code: z
        .string()
        .trim()
        .toLowerCase()
        .min(2)
        .max(40)
        .regex(/^[a-z0-9_]+$/)
        .optional(),
    groupModule: z.enum(MODULE_CODES).optional(),
    name: z.string().trim().min(2).max(120),
    description: z.string().max(400).optional(),
    scope: z.enum(['tenant', 'branch', 'department', 'self']).default('branch'),
    require2fa: z.boolean().default(false),
    permissions: z.array(z.string().min(3).max(60)).default([]),
    modules: z.array(z.enum(MODULE_CODES)).max(40).default([]),
});
export const setRolePermissionsSchema = z.object({
    permissions: z.array(z.string().min(3).max(60)).max(400),
    reason: z.string().trim().max(400).optional(),
});
export const BRANDING_LOGO_PATH = /^\/api\/v1\/public\/branding\/([0-9a-f-]{36})\/[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/;
export function brandingLogoTenant(value) {
    return BRANDING_LOGO_PATH.exec(value)?.[1] ?? null;
}
export const tenantSettingsSchema = z.object({
    displayName: z.string().trim().min(2).max(160).optional(),
    logoUrl: z.string().max(500).nullable().optional(),
    primaryColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    letterheadHtml: z.string().max(20000).nullable().optional(),
    letterhead: letterheadSchema.optional(),
    locale: z
        .enum(['en', 'ur', 'ur-Latn', 'ar', 'es'])
        .transform((value) => (value === 'ur' ? 'ur-Latn' : value))
        .optional(),
    taxDocumentsSecondaryLanguage: z.boolean().optional(),
    clinicalDocumentsSecondaryLanguage: z.boolean().optional(),
    /** Consultation follow-up window in days during which a revisit is free. */
    freeFollowUpDays: z.number().int().min(0).max(120).optional(),
    paidNoShowPolicy: z.enum(PAID_NO_SHOW_POLICY).optional(),
    followUpEngine: z.enum(FOLLOW_UP_ENGINE).optional(),
    followUpValidDays: followUpValidDaysSchema.optional(),
    walkInWithoutSession: z.boolean().optional(),
    screeningBeforeReady: z.enum(SCREENING_BEFORE_READY).optional(),
    queueOrder: z.enum(QUEUE_ORDER).optional(),
    portalMaxUpcomingPerPatient: z.number().int().min(1).max(20).optional(),
    quoteValidDays: z.number().int().min(0).max(90).optional(),
    tokenAfterPayment: z.boolean().optional(),
    dayClosesAt: z.number().int().min(0).max(8).optional(),
    autoNoShow: z.boolean().optional(),
    autoNoShowEnabledAt: z.string().datetime().optional(),
    backEntryDays: z.number().int().min(0).max(30).optional(),
    requirePrimaryDiagnosis: z.boolean().optional(),
    insurancePreauthMode: z.enum(INSURANCE_MODES).nullable().optional(),
    reminderHours: reminderHoursSchema.optional(),
    /** Refuse selling and taking cash unless the cashier has an open shift. */
    requireOpenShift: z.boolean().optional(),
    /** Supplier payments above this need a second person with supplier.payment.approve; unset means no limit. */
    supplierPaymentApprovalLimitMinor: z.number().int().min(0).max(10_000_000_000).nullable().optional(),
    /** Write-offs and adjustments worth more than this need stock.adjust.approve from someone else; unset means no limit. */
    stockAdjustApprovalLimitMinor: z.number().int().min(0).max(10_000_000_000).nullable().optional(),
    /** Pharmacy sales returns worth more than this need a supervisor with discount.approve; unset means no limit. */
    posReturnApprovalLimitMinor: z.number().int().min(0).max(10_000_000_000).nullable().optional(),
    allowCashWithoutShift: z.boolean().optional(),
    requireTxnRef: z.boolean().optional(),
    blindCashCount: z.boolean().optional(),
    vatNumber: z.preprocess((v) => (v === '' ? null : v), z.string().trim().regex(/^[0-9A-Za-z-]{5,20}$/, 'Use the 15-digit VAT registration number').nullable().optional()),
    sellerName: z.preprocess((v) => (v === '' ? null : withoutBidiControls(v)), z.string().trim().min(2).max(160).nullable().optional()),
    sellerNameAr: z.preprocess((v) => (v === '' ? null : withoutBidiControls(v)), z.string().trim().min(2).max(160).nullable().optional()),
    crNumber: z.preprocess((v) => (v === '' ? null : v), z.string().trim().regex(/^[0-9A-Za-z-]{4,20}$/, 'Letters, digits and dashes only').nullable().optional()),
    /** Co-sign rules for medical officers (SRS 4.3.3). */
    requireCosignForMedicalOfficer: z.boolean().optional(),
    /** Ward stock model for the pharmacy IPD path (SRS 7.3). */
    wardStockModel: z.enum(['unit_dose', 'ward_stock']).optional(),
    ...ipdScheduleSettingsSchema.shape,
    staffAlertSmsEscalation: z.boolean().optional(),
    staffAlertSmsDelayMinutes: z.number().int().min(STAFF_ALERT_SMS_DELAY_MIN).max(STAFF_ALERT_SMS_DELAY_MAX).optional(),
    sessionTimeoutMinutes: z.number().int().min(5).max(24 * 60).optional(),
    enforce2faForAdmins: z.boolean().optional(),
    ipAllowlist: z.array(z.string().max(64)).max(50).optional(),
    numberPatterns: z.record(z.string(), z.string().max(60)).optional(),
    labelWidthMm: z.number().min(20).max(120).optional(),
    labelHeightMm: z.number().min(10).max(120).optional(),
    labelCommandLang: z.enum(['TSPL', 'ESCPOS']).optional(),
    printBridgePort: z.number().int().min(1024).max(65535).optional(),
    analyserResultsEditable: z.boolean().optional(),
    criticalResultEscalationPhone: z.preprocess((v) => (v === '' ? null : v), phoneSchema.nullable().optional()),
    separateVerifier: z.boolean().optional(),
    qcWindowHours: z.number().int().min(1).max(168).optional(),
    criticalEscalationMinutes: z.number().int().min(5).max(1440).optional(),
    billOverlappingPanels: z.boolean().optional(),
    accessionPerOrder: z.boolean().optional(),
    releaseRequiresPayment: z.boolean().optional(),
    statCollectionMinutes: z.number().int().min(1).max(240).optional(),
    reportReadySms: z.enum(LAB_REPORT_READY_SMS).optional(),
    accessionMode: z.enum(LAB_ACCESSION_MODES).optional(),
    tubeSuffix: z.enum(LAB_TUBE_SUFFIXES).optional(),
    homeCollectionEnabled: z.boolean().optional(),
    collectionIdentityCheck: z.enum(['off', 'on']).optional(),
    collectRequiresPayment: z.boolean().optional(),
    analyserOverride: z.enum(LAB_ANALYSER_OVERRIDE).optional(),
    labelReprintApproval: z.boolean().optional(),
    labelShowDob: z.boolean().optional(),
    labelShowNationalId: z.boolean().optional(),
    reportHeaderMode: z.enum(['letterhead', 'preprinted']).optional(),
    preprintedTopMm: z.number().min(0).max(120).optional(),
    labReportLanguage: z.enum(LAB_REPORT_LANGUAGES).nullable().optional(),
    instrumentLogDays: z.number().int().min(7).max(3650).optional(),
    radiologySeparateVerifier: z.boolean().nullable().optional(),
    publicPortalUrl: z.string().trim().url().max(200).nullable().optional(),
});
//# sourceMappingURL=org.js.map